<template>
  <tr v-if='editLine || editMode'>
    <td>
      <v-form v-model='isDescriptionValid'>
        <v-text-field dense single-line hide-details v-model='line.description' ref='description' class='body-2'
          :rules='[rules.required]'>
          <template v-slot:append-outer>
            <wc-pick-inventory @selected='updateLineWithInventory'></wc-pick-inventory>
          </template>
        </v-text-field>
      </v-form>
    </td>
    <td>
      <v-form v-model='isAccountValid'>
        <v-autocomplete :items='revenue_accounts' v-model='line.account' class='body-2' single-line item-value='id'
          item-text='name' hide-details dense :rules='[rules.required]'>
          <template v-slot:item='data'>
            <account-picker-list-item :account='data.item'></account-picker-list-item>
          </template>
        </v-autocomplete>
      </v-form>
    </td>
    <td align='right'>
      <v-tooltip top>
        <template v-slot:activator='{ on }'>
          <v-chip v-on='on' x-small label outlined @click='line.is_warranty = !line.is_warranty'
            :color="line.is_warranty ? 'error' : 'grey lighten-1'">
            Warranty
          </v-chip>
        </template>
        <span v-if='line.is_warranty'>Is Warranty</span>
        <span v-else>Not Warranty</span>
      </v-tooltip>


      <v-tooltip top>
        <template v-slot:activator='{ on }'>
          <v-chip v-on='on' x-small label outlined class='ml-1' @click='line.has_gst = !line.has_gst'
            :color="line.has_gst ? 'info' : 'grey lighten-1'">
            GST
          </v-chip>
        </template>
        <span v-if='line.has_gst'>Has GST</span>
        <span v-else>No GST</span>
      </v-tooltip>
    </td>
    <td>
      <v-form v-model='isQtyValid'>
        <v-text-field id='quantity' dense single-line reverse hide-details class='body-2' @focus='selectQuantity'
          :rules='[rules.decimal]' v-model='line.qty'></v-text-field>
      </v-form>
    </td>
    <td>
      <v-form v-model='isPriceValid'>
        <v-text-field id='price' dense hide-details reverse class='body-2' @focus='selectPrice' :rules='[rules.decimal]'
          v-model='line.price'></v-text-field>
      </v-form>
    </td>
    <td>
      <v-text-field v-if='isQtyValid && isPriceValid' dense single-line hide-details reverse class='body-2'
        :value='subtotal' readonly></v-text-field>
    </td>
    <td>
      <v-text-field v-if='isQtyValid && isPriceValid' dense single-line hide-details reverse class='body-2' :value='gst'
        readonly></v-text-field>
    </td>
    <td valign='middle' align='middle'>
      <v-btn x-small :disabled='savingLine' color='error' dark class='mr-1' @click='cancel'>
        <v-icon small>mdi-close</v-icon>
      </v-btn>
      <template v-if='isDescriptionValid && isAccountValid && isQtyValid && isPriceValid'>
        <v-btn x-small :loading='savingLine' color='success' @click='saveLine'>
          <template v-slot:loader>
            <v-progress-circular color='white' indeterminate :size='14' :width='1'></v-progress-circular>
          </template>
          <v-icon small>mdi-check</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-tooltip top>
          <template v-slot:activator='{ on }'>
            <v-btn v-on='on' x-small color='error'>
              <v-icon small>mdi-alert-circle</v-icon>
            </v-btn>
          </template>
          <span>Error in description, qty or price</span>
        </v-tooltip>
      </template>
    </td>
  </tr>

  <tr v-else>
    <td>{{ line.description }}</td>
    <td>{{ line.account_obj ? line.account_obj.name : '' }}</td>
    <td class='text-right'>
      <v-tooltip top>
        <template v-slot:activator='{ on }'>
          <v-chip v-on='on' x-small label outlined :color="line.is_warranty ? 'error' : 'grey lighten-1'">
            Warranty
          </v-chip>
        </template>
        <span v-if='line.is_warranty'>Is Warranty</span>
        <span v-else>Not Warranty</span>
      </v-tooltip>
      <v-tooltip top>
        <template v-slot:activator='{ on }'>
          <v-chip v-on='on' x-small label outlined class='ml-1' :color="line.has_gst ? 'info' : 'grey lighten-1'">
            GST
          </v-chip>
        </template>
        <span v-if='line.has_gst'>Has GST</span>
        <span v-else>No GST</span>
      </v-tooltip>
    </td>
    <td class='text-right'>
      {{ line.qty | dec }}
    </td>
    <td class='text-right'>
      {{ line.price | dec }}
    </td>
    <td class='text-right'>
      {{ subtotal }}
    </td>
    <td class='text-right'>
      {{ line.tax | dec }}
    </td>
    <td class='text-right'>
      <wc-line-actions v-if='!disableEdit' :disableSortUp='disableSortUp' :disableSortDown='disableSortDown'
        @edit='setupEdit' @sortUp="$emit('sortUp')" @sortDown="$emit('sortDown')" @deleteLine='delete_line'>
      </wc-line-actions>
    </td>
  </tr>
</template>

<script>
import _ from 'lodash';
import formRules from '@/mixins/formRules';
import WCPickInventoryButton from '@/modules/assets/components/WCPickInventoryButton';
import AccountPickerListItem from '@/modules/accounting/components/AccountPickerListItem';
import accountingAPI from '@/api/accounting';

export default {
  name: 'invoice-line',
  mixins: [formRules],
  props: {
    disableEdit: {
      required: false,
      type: Boolean,
      default: false
    },
    line: {
      required: true
    },
    editMode: {
      required: false,
      type: Boolean,
      default: false
    },
    disableSortUp: {
      required: false,
      type: Boolean,
      default: true
    },
    disableSortDown: {
      required: false,
      type: Boolean,
      default: true
    },
    cancelEdit: {
      required: false
    },
    revenue_accounts: {
      required: true,
      type: Array
    }
  },
  data: () => ({
    new_line: false,
    line_copy: {},
    editLine: false,
    savingLine: false,
    isAccountValid: false,
    isDescriptionValid: false,
    isQtyValid: false,
    isPriceValid: false,
    inputClass: 'body-2'
  }),
  computed: {
    rawSubtotal() {
      if (this.line.is_warranty) {
        return 0;
      }
      return this.line.qty * this.line.price;
    },
    gst_icon() {
      if (this.editLine) {
        if (this.line.has_gst) {
          return 'mdi-alpha-g-box';
        }
        return 'mdi-alpha-g-box-outline';
      }
      return '';
    },
    rawTotal() {
      if (this.line.is_warranty) {
        return 0;
      }
      return parseFloat(this.rawSubtotal);
    },
    rawGst() {
      if (this.line.has_gst) {
        return _.multiply(this.rawSubtotal, this.$store.state.misc.settings.tax_rate_perc);
      }
      return 0;
    },
    subtotal() {
      return this.$options.filters.dec(this.rawSubtotal);
    },
    gst() {
      return this.$options.filters.dec(this.rawGst);
    },
    total() {
      return this.$options.filters.dec(this.rawTotal);
    }
  },
  watch: {
    editMode() {
      if (this.editMode) {
        this.setupEdit();
      }
    }
  },
  created() {
    this.check_if_new_line();
  },
  methods: {
    setupEdit() {
      this.editLine = true;
      this.line_copy = _.cloneDeep(this.line);
      setTimeout(() => {
        this.$refs.description.focus();
      }, 100)
    },
    check_if_new_line() {
      if (this.line.id === null || this.line.id === undefined) {
        this.new_line = true;
      }
    },
    saveLine() {
      if (this.new_line === true) {
        this.new_line = false;
        this.disable_up = this.disableSortUp;
        this.disable_down = this.disableSortDown;
        this.create();
      } else {
        this.update();
      }
    },
    async update() {
      const response = await accountingAPI.updateInvoiceLineItem(this.line.id, this.line);
      this.$emit('updateLine', this.line_copy, response);
      this.line_copy = _.cloneDeep(this.line);
      this.editLine = false;
    },
    async create() {
      const res = await accountingAPI.createInvoiceLineItem(this.line);
      this.$emit('push_line', this.line, res);
      this.editLine = false;
    },
    updateLineWithInventory(invoice) {
      this.line.description = invoice.description;
      this.line.price = invoice.estimate_price;
      this.$refs.description.focus();
    },
    cancel() {
      this.$emit('cancelEdit', this.line_copy);
      this.editLine = false;
    },
    delete_line() {
      this.$emit('deleteLine', this.line);
    },
    selectQuantity() {
      setTimeout(() => {
        document.getElementById('quantity').select()
      }, 50)
    },
    selectPrice() {
      setTimeout(() => {
        document.getElementById('price').select()
      }, 50)
    },
  },
  components: {
    'wc-pick-inventory': WCPickInventoryButton,
    'account-picker-list-item': AccountPickerListItem
  }
}
</script>
